// Client API — helpers pour fetch avec cookies httpOnly (auth JWT)
// Cf. SPECS §2.1 (JWT httpOnly).
//
// Tous les endpoints sont relatifs à /api/v1 ou /exports selon le préfixe
// backend (cf. backend/app/api/v1/interventions.py + exports.py).
//
// L'URL de base est centralisée dans "@/lib/apiBase" :
//   - dev  : http://localhost:18000/api (backend en direct)
//   - prod : VITE_API_URL fourni au build (ARG docker/frontend/Dockerfile)
//            ex: https://maintenance.opsvox.com/api
import { API_BASE } from "@/lib/apiBase";

async function fetchJson<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: "include", // envoie les cookies httpOnly
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) {
    const raw = await res.text();
    // Tenter de parser le JSON d'erreur FastAPI : { detail: "..." } ou { detail: [{ msg: "..." }] }
    let msg = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed.detail) {
        if (typeof parsed.detail === "string") {
          msg = parsed.detail;
        } else if (Array.isArray(parsed.detail) && parsed.detail[0]?.msg) {
          msg = parsed.detail.map((d: { msg: string }) => d.msg).join(", ");
        }
      }
    } catch {
      // raw n'est pas du JSON, on garde le texte brut
    }
    throw new Error(msg);
  }
  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ─── Interventions ─────────────────────────────────────────────────────

import type {
  InterventionListResponse,
  InterventionRead,
  InterventionDetailRead,
  StoreInterventionInput,
} from "@/types/intervention";

export const interventionsApi = {
  list: (params: Record<string, string | number | undefined> = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") search.append(k, String(v));
    });
    const qs = search.toString();
    return fetchJson<InterventionListResponse>(
      `/v1/interventions${qs ? "?" + qs : ""}`,
    );
  },

  get: (id: number) =>
    fetchJson<InterventionDetailRead>(`/v1/interventions/${id}`),

  create: (data: StoreInterventionInput) =>
    fetchJson<InterventionRead>(`/v1/interventions`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<StoreInterventionInput>) =>
    fetchJson<InterventionRead>(`/v1/interventions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  annuler: (id: number) =>
    fetchJson<InterventionRead>(`/v1/interventions/${id}/annuler`, {
      method: "POST",
    }),

  remove: (id: number) =>
    fetchJson<void>(`/v1/interventions/${id}`, {
      method: "DELETE",
    }),

  cyclePreventif: (engin_id: number, date_prevue: string) =>
    fetchJson<{
      en_retard: boolean;
      ecart_jours: number;
      statut: string;
      derniere_intervention?: string;
    }>(
      `/v1/interventions/cycle-preventif/info?engin_id=${engin_id}&date_prevue=${date_prevue}`,
    ),
};

// ─── Exports PDF ───────────────────────────────────────────────────────

export const exportsApi = {
  interventionsPdfUrl: (params: Record<string, string | number | undefined> = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") search.append(k, String(v));
    });
    const qs = search.toString();
    return `${API_BASE}/v1/exports/interventions/pdf${qs ? "?" + qs : ""}`;
  },

  ficheViergeUrl: () => `${API_BASE}/v1/exports/interventions/fiche-vierge`,
};
// ─── Articles + Stocks ─────────────────────────────────────────────────

import type { Article, StockMovement } from "@/types/article";

export const articlesApi = {
  list: () => fetchJson<Article[]>(`/v1/articles`),
  create: (data: Omit<Article, "id">) =>
    fetchJson<Article>(`/v1/articles`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: number, data: Partial<Omit<Article, "id">>) =>
    fetchJson<Article>(`/v1/articles/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  delete: (id: number) =>
    fetchJson<void>(`/v1/articles/${id}`, { method: "DELETE" }),
};

export const stocksApi = {
  current: (article_id: number) =>
    fetchJson<{ article_id: number; stock_actuel: number }>(
      `/v1/articles/${article_id}/stock`,
    ),
  movements: (article_id: number) =>
    fetchJson<StockMovement[]>(`/v1/articles/${article_id}/movements`),
  createMovement: (
    article_id: number,
    data: {
      type: "entree" | "sortie" | "ajustement";
      quantite: number;
      motif?: string;
      intervention_id?: number;
    },
  ) =>
    fetchJson<StockMovement>(`/v1/articles/${article_id}/movements`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// ─── Engins & Types ────────────────────────────────────────────────────

import type { TypeEngin, Engin } from "@/types/engin";

export const enginsApi = {
  list: () => fetchJson<Engin[]>(`/v1/engins`),
  get: (id: number) => fetchJson<Engin>(`/v1/engins/${id}`),
  create: (data: Partial<Engin>) => fetchJson<Engin>(`/v1/engins`, { method: "POST", body: JSON.stringify(data) }),
  update: (id: number, data: Partial<Engin>) =>
    fetchJson<Engin>(`/v1/engins/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: number) => fetchJson<void>(`/v1/engins/${id}`, { method: "DELETE" }),
};

export const typeEnginsApi = {
  list: () => fetchJson<TypeEngin[]>(`/v1/type-engins`),
  create: (data: { name: string; icone: string }) =>
    fetchJson(`/v1/type-engins`, { method: "POST", body: JSON.stringify(data) }),
  update: (id: number, data: Partial<{ name: string; statut: string; icone: string }>) =>
    fetchJson(`/v1/type-engins/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: number) => fetchJson<void>(`/v1/type-engins/${id}`, { method: "DELETE" }),
};

// ─── Agenda + Dashboard ───────────────────────────────────────────────

import type { CycleResult } from "@/types/agenda";

export const agendaApi = {
  cyclePreventif: (engin_id: number, date_prevue: string) =>
    fetchJson<CycleResult>(
      `/v1/interventions/cycle-preventif/info?engin_id=${engin_id}&date_prevue=${date_prevue}`,
    ),
  cyclePreventifAll: () =>
    fetchJson<
      Array<{
        engin_id: number;
        engin_code: string;
        statut: "OK" | "En retard" | "Non planifié";
        ecart_jours: number;
        derniere_intervention: string | null;
        date_prevue: string | null;
      }>
    >(`/v1/interventions/cycle-preventif/all`),
};

export const dashboardApi = {
  summary: () => fetchJson<{
    interventions: {
      total: number;
      preventives: number;
      curatives: number;
      terminees: number;
      avec_da: number;
    };
    demandes_achat: { actives: number; emises: number };
  }>(`/v1/dashboard/summary`),
};

// ─── Demandes d'Achat ──────────────────────────────────────────────────

import type {
  DemandeAchat,
  DemandeAchatDetail,
  StoreDAInput,
} from "@/types/da";

export const demandesAchatApi = {
  list: () => fetchJson<DemandeAchat[]>(`/v1/demandes-achat`),
  get: (id: number) => fetchJson<DemandeAchatDetail>(`/v1/demandes-achat/${id}`),
  create: (data: StoreDAInput) =>
    fetchJson<DemandeAchatDetail>(`/v1/demandes-achat`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  valider: (id: number) =>
    fetchJson<DemandeAchat>(`/v1/demandes-achat/${id}/valider`, {
      method: "POST",
    }),
  annuler: (id: number) =>
    fetchJson<DemandeAchat>(`/v1/demandes-achat/${id}/annuler`, {
      method: "POST",
    }),
  update: (id: number, data: Partial<StoreDAInput>) =>
    fetchJson<DemandeAchatDetail>(`/v1/demandes-achat/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  exportPdfUrl: (id: number) =>
    `${API_BASE}/v1/exports/demandes-achat/${id}/pdf`,
  exportExcelUrl: (id: number) =>
    `${API_BASE}/v1/exports/demandes-achat/${id}/excel`,
};
